// src/AuthContext.tsx
import React, { createContext, useEffect, useState } from 'react';
import { FormInputs } from '../types/session-forms.types';
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firebase-firestore";
import { Collections } from '../types/collection';
import { Session } from '../features/session/hooks/useFetchSessions';
import { captureException } from "@sentry/react";

interface SessionContextProps {
    sessions: Session[] | null;
    fetch: () => void;
    loading: boolean;
}

export const SessionContext = createContext<SessionContextProps>({
    sessions: [],
    fetch: () => { },
    loading: false,
});

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [sessions, setSessions] = useState<Session[] | null>(null);
    const [triggerData, setTriggerData] = useState<number>(new Date().getTime());
    const [loading, setLoading] = useState<boolean>(false);
    useEffect(() => {
        async function getUserSessions() {
            try {
                const userId = await firebase.auth().currentUser?.uid;
                if (!userId) return;
                setLoading(true);
                await firebase.firestore()
                    .collection(Collections.Sessions).doc(userId).collection('list')
                    .onSnapshot((snapshot) => {
                        if (snapshot.empty) {
                            setLoading(false);
                            setSessions([])
                            return;
                        }
                        const results: FormInputs[] = [];
                        const tempSessions: Session[] = [];
                        const listData = snapshot.docs.reverse();
                        for (let i = 0; i < listData.length; i++) {
                            const data = listData[i].data() as FormInputs;
                            results.push(data);
                            tempSessions.push({
                                id: listData[i].id,
                                title: data?.jobRole?.label || data.title,
                                date: data.createdAt || '',
                                type: data.sessionType?.label || '',
                                score: data.score || 0,
                                status: data.status,
                                completed: data.completed || false,
                                interviewType: data.interviewType
                            })
                        }
                        setSessions(tempSessions);
                        setLoading(false);
                    })
                setLoading(false);
            } catch (error) {
                setLoading(false);
                captureException(error);
            }
        }
        getUserSessions();
    }, [triggerData])

    const triggerFetch = () => {
        setTriggerData(new Date().getTime())
    }

    return (
        <SessionContext.Provider
            value={{
                sessions,
                fetch: triggerFetch,
                loading,
            }}
        >
            {children}
        </SessionContext.Provider>
    );
};
